
import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';

import { fetchPageData } from '../../../../api/utils';
import SubPagesLayout from '../../../UI/layout/subPages';
import InstructorAssignmentForm from '../../../UI/instructor/settings/assignments/form';

import { assignmentTypeMappings } from '../../../../vars';
import { formatScnearioLevelToFilterName, shuffleArray, getTargetEntityType, matchesEntityType } from '../../../../utils';

import {
  navigation,
  gameCreateItemsQuestionsColumns,
  gameCreateItemsOptionsClassification,
  gameCreateItemsOptionsNormal,
  scenarioLevelsDescription,
  designTypesMap
} from './data';

import {
  generalInfoQuery,
  instructorScenarioLevelsQuery,
  coursesSetupQuery,
  updateTournament,
  createScenarioLevels,
  editScenarioLevel,
  createItemQuestionsBulk,
  deleteItemOptions,
  deleteItemQuestions,
  createItemOptionsBulk,
  deleteScenarioLevel,
  scenarioAddCourse,
  transactionsQuery,
  tournamentsRoundsQuery,
  updateTournamentsRounds,
} from './api';

// Create-flow endpoints live in the review-session onboarding api module
// (that's still their canonical home even though the wizard UI is being
// retired in favor of this in-place create mode). Reuse them here so the
// setup/ + create-full/ shapes stay in one place.
import {
  createTournaments,
  createGameFull,
  setupTournament,
  getTournamentsRounds as getReviewSessionRounds,
  getTournamentsTeams as getReviewSessionTeams,
} from '../../onboarding-review-session/api';
import { teamNames as REVIEW_SESSION_TEAM_NAMES } from '../../onboarding-review-session/data';

import TournamentEditPanel from '../../../UI/instructor/settings/assignments/tournamentEditPanel';

// March Madness bracket names, listed final-round-last. slice(-N) gives
// the last N so a 3-round session lands as [Elite 8, Final 4, Championship]
// and Championship is always the last round.
const BRACKET_ROUND_NAMES = ['Sweet 16', 'Elite 8', 'Final 4', 'Championship'];

// Canonical exercise order used when creating rounds: the instructor's
// checkbox selection is normalized to this sequence before rounds are
// assigned, so Classification is always Round 1 when selected, then
// Normal, then BE, then Principles.
const CANONICAL_GAME_ORDER = ['classification', 'normal', 'equation', 'principles'];

const GAME_TYPE_TO_ENUM = {
  classification: 'ACC',
  normal: 'NOR',
  equation: 'BE',
  principles: 'PR',
};

// Backend accepts the short enum on POST (create-full) but echoes the
// long display name on GET (general-info.games[].game_type). Cache
// patches downstream of the create-full response must use the long
// name so tournamentRoundGames and RoundCard titles render the label
// the rest of the app already expects.
const GAME_TYPE_TO_FULL_NAME = {
  classification: 'Account Classification',
  normal: 'Normal Balance',
  equation: 'Balanced Equation',
  principles: 'The Principles',
};

export default function InstructorSettingsEditTournament() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const params = useParams();
  const tournamentId = Number(params.tournamentId);
  // /instructor/settings/tournament/new mounts the same component without
  // a :tournamentId param. In that mode Step 1 becomes interactive, the
  // Steps 2 & 3 section is hidden, and the CTA below Step 1 runs the
  // create flow (tournament + games + setup) instead of any per-level work.
  const isCreateMode = !params.tournamentId;

  const [isLoading, setIsLoading] = useState(false);
  const [isCreateSubmitting, setIsCreateSubmitting] = useState(false);
  const [selectedGameKeys, setSelectedGameKeys] = useState([]);
  const [createErrorMessage, setCreateErrorMessage] = useState('');

  const { data: generalInfo } = useQuery(generalInfoQuery());
  const sessions = generalInfo?.sessions;

  const tournament = useMemo(
    () => generalInfo?.tournaments?.find(t => t.id === tournamentId),
    [generalInfo?.tournaments, tournamentId]
  );

  const { data: tournamentsRounds, refetch: refetchTournamentsRounds } = useQuery(tournamentsRoundsQuery({tournamentId}));

  const [formData, setFormData] = useState({});
  const [scenarioId, setScenarioId] = useState(0);
  const [roundRows, setRoundRows] = useState([]);

  // One source-template scenario per game type — replaces the prior
  // multi-source lookups (entity-type / special-accounts for class+
  // normal, be-corporate / be-noncorporate for equation, principles-
  // corporate / principles-noncorporate / principles-assumptions-
  // constraints for principles). The "Add a Level" modal pulls
  // templates from these single scenarios; level creation copies
  // questions/options off the matching level inside.
  const [classificationGameScenarioId, setClassificationGameScenarioId] = useState(null);
  const [normalGameScenarioId, setNormalGameScenarioId] = useState(null);
  const [beGameScenarioId, setBeGameScenarioId] = useState(null);
  const [principlesGameScenarioId, setPrinciplesGameScenarioId] = useState(null);

  const [editingScenarioLevelIndex, setEditingScenarioLevelIndex] = useState(null);
  const [editingScenarioLevelPrecheckedItems, setEditingScenarioLevelPrecheckedItems] = useState([]);
  const [editingScenarioLevelPrecheckedDesigns, setEditingScenarioLevelPrecheckedDesigns] = useState([]);

  const [isGameStarted, setIsGameStarted] = useState(false);
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [selectedGameType, setSelectedGameType] = useState('');

  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [isImDoneButtonLoading, setIsImDoneButtonLoading] = useState(false);

  // Active editing target for the new round-first layout. `null` when no
  // modal is open; { round, editingScenarioLevelIndex } when the
  // instructor is adding (index=null) or editing an existing level on a
  // specific round.
  const [editingContext, setEditingContext] = useState(null);
  
  const { data: scenarioLevels, refetch: refetchScenarioLevels } = useQuery(instructorScenarioLevelsQuery({scenarioId}));

  const { data: classificationGameScenarioLevels } = useQuery(instructorScenarioLevelsQuery({ scenarioId: classificationGameScenarioId }));
  const { data: normalGameScenarioLevels } = useQuery(instructorScenarioLevelsQuery({ scenarioId: normalGameScenarioId }));
  const { data: beGameScenarioLevels } = useQuery(instructorScenarioLevelsQuery({ scenarioId: beGameScenarioId }));
  const { data: principlesGameScenarioLevels } = useQuery(instructorScenarioLevelsQuery({ scenarioId: principlesGameScenarioId }));

  // Use the course-setup endpoint (same as add.js / edit.js) — returns
  // just the seeded template scenarios in one call instead of paging
  // through the full scenarios table.
  const { data: scenarios, refetch: refetchScenariosQuery } = useQuery(coursesSetupQuery());
  const { data: transactions, refetch: refetchTransactionsQuery } = useQuery(transactionsQuery());
  
  const courseMembers = generalInfo?.users;

  const tournamentRoundGames = useMemo(() => {
    if (!tournamentsRounds?.length || !generalInfo?.games) return [];
    return tournamentsRounds.map(round => {
      const gameData = generalInfo.games.find(g => g.id === round.game_id);
      return {
        ...round,
        gameType: gameData?.game_type || null,
        scenarioId: gameData?.scenario,
      };
    });
  }, [tournamentsRounds, generalInfo?.games]);

  // Fetch scenario levels for every round in parallel so the new
  // round-first UI can render all rounds' level pills at once. Cache
  // key per scenarioId matches the existing instructorScenarioLevelsQuery
  // used elsewhere, so this shares hits with the single-query version
  // still fired via `scenarioId` state (kept for the save/delete
  // handlers that read the current `scenarioLevels`).
  const perRoundLevelsQueries = useQueries({
    queries: tournamentRoundGames.map(round => ({
      ...instructorScenarioLevelsQuery({ scenarioId: round.scenarioId }),
      enabled: !!round.scenarioId,
    })),
  });

  const scenarioLevelsByRound = useMemo(() => {
    const map = {};
    tournamentRoundGames.forEach((round, idx) => {
      map[round.id] = perRoundLevelsQueries[idx]?.data || [];
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentRoundGames, perRoundLevelsQueries.map(q => q.dataUpdatedAt).join(',')]);

  const loadingByRound = useMemo(() => {
    const map = {};
    tournamentRoundGames.forEach((round, idx) => {
      map[round.id] = !!perRoundLevelsQueries[idx]?.isLoading;
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentRoundGames, perRoundLevelsQueries.map(q => q.isLoading).join(',')]);

  // Map of gameId -> "students have started" bool. Used to disable
  // level editing per round once any student session exists for that
  // game (mirrors the isGameStarted single-flag check used previously
  // for the currently-selected game).
  const isGameStartedByGame = useMemo(() => {
    if (!sessions || !generalInfo?.users) return {};
    const currentUserId = generalInfo.users.id;
    const map = {};
    tournamentRoundGames.forEach(round => {
      if (!round.game_id) return;
      map[round.game_id] = sessions.some(
        s => s.user !== currentUserId && s.game === round.game_id
      );
    });
    return map;
  }, [sessions, generalInfo?.users, tournamentRoundGames]);
  
  const updateTournamentMutation = useMutation({
    mutationFn: (data) => updateTournament(data)
  });

  const createScenarioLevelsMutation = useMutation({
    mutationFn: (data) => createScenarioLevels(data)
  });

  const editScenarioLevelsMutation = useMutation({
    mutationFn: (data) => editScenarioLevel(data)
  });
  
  const createItemQuestionsBulkMutation = useMutation({
    mutationFn: (data) => createItemQuestionsBulk(data)
  });
  
  const deleteItemQuestionsMutation = useMutation({
    mutationFn: (data) => deleteItemQuestions(data)
  });
  
  const createItemOptionsBulkMutation = useMutation({
    mutationFn: (data) => createItemOptionsBulk(data)
  });
  
  const deleteItemOptionsMutation = useMutation({
    mutationFn: (data) => deleteItemOptions(data)
  });

  const deleteScenarioLevelMutation = useMutation({
    mutationFn: (data) => deleteScenarioLevel(data)
  });

  const updateTournamentsRoundsMutation = useMutation({
    mutationFn: (data) => updateTournamentsRounds(data)
  });

  const scenarioAddCourseMutation = useMutation({
    mutationFn: (data) => scenarioAddCourse(data)
  });

  const createTournamentsMutation = useMutation({
    mutationFn: (data) => createTournaments(data)
  });

  const createGameFullMutation = useMutation({
    mutationFn: (data) => createGameFull(data)
  });

  const setupTournamentMutation = useMutation({
    mutationFn: (data) => setupTournament(data)
  });


  useEffect(() => {
    if (sessions && selectedGameId && generalInfo?.users) {
      const currentUser = generalInfo.users;
      const isCurrentGameSession = sessions.some(
        session => session.user !== currentUser.id && session.game === selectedGameId
      );
      setIsGameStarted(isCurrentGameSession);
    }
  }, [sessions, selectedGameId, generalInfo?.users]);

  useEffect(() => {
    if (tournament) {
      setFormData({
        name: tournament.name || '',
        assignmentType: 'Tournament'
      });
    }
  }, [tournament]);

  // In create mode, pre-fill the name as "Review Session <N>" where N is
  // one more than the number of tournaments already on this instructor's
  // primary course. Only sets the initial draft; instructor edits win.
  useEffect(() => {
    if (!isCreateMode) return;
    if (formData.name) return;
    const currentCourseId = generalInfo?.courses?.[0]?.id;
    if (!currentCourseId || !Array.isArray(generalInfo?.tournaments)) return;
    const tournamentsForCourse = generalInfo.tournaments.filter(
      t => t.course_id === currentCourseId
    ).length;
    setFormData({
      name: `Review Session ${tournamentsForCourse + 1}`,
      assignmentType: 'Tournament',
    });
  }, [isCreateMode, formData.name, generalInfo?.courses, generalInfo?.tournaments]);

  useEffect(() => {
    if (tournamentRoundGames?.length && (!selectedGameType || !selectedGameId || !scenarioId)) {
      setSelectedGameId(tournamentRoundGames[0]?.game_id);
      setSelectedGameType(tournamentRoundGames[0]?.gameType);
      setScenarioId(tournamentRoundGames[0]?.scenarioId);
    }
  }, [tournamentRoundGames, selectedGameType, selectedGameId, scenarioId]);

  const getRoundRows = (tournamentsRounds) => {
    const ordinals = ['First', 'Second', 'Third', 'Final'];

    return tournamentsRounds.map((r, i, arr) => ({
      id: r.id,
      name: `${i === arr.length - 1 ? 'Final' : ordinals[i]} Round`,
      stage: `(${r.name})`,
      gameId: r.game_id,
      index: r.number,
      changed: false,
      disabled: r.is_active || r.is_finished
    }));
  };

  useEffect(() => {
    if(tournament && tournamentsRounds?.length) {
      const roundRows = getRoundRows(tournamentsRounds);
      setRoundRows(roundRows);
    }
  }, [tournament, tournamentsRounds]);

  const handleEditTournamentSettings = async ({
    name,
    formSavedCallback
  }) => {
    setIsLoading(true);

    if(formData.name !== name) {
      await updateTournamentMutation.mutateAsync({
        id: tournamentId,
        name
      }).catch((error) => {
        console.error('Caught error with mutateAsync:', error);
      });
    }

    if(formSavedCallback) {
      formSavedCallback();
    }
    setIsLoading(false);
  }

  const handleShowLevelAccountsButtonClick = (scenarioLevelIndex) => {
    setEditingScenarioLevelIndex(scenarioLevelIndex);
    const edititngSceenarioLevel = scenarioLevels[scenarioLevelIndex];

    const savedAccountItems = gameTypeShortened?.includes('classification')
      ? edititngSceenarioLevel?.questions.map(({ id, account_item }) => ({id, text: account_item.name}))
      : gameTypeShortened?.includes('normal')
      ? edititngSceenarioLevel?.normal_questions.map(({ id, account_item }) => ({id, text: account_item.name}))
      : gameTypeShortened?.includes('equation')
      ? edititngSceenarioLevel?.be_questions.map(({ id, description }) => ({id, text: description}))
      : gameTypeShortened?.includes('principles')
      ? edititngSceenarioLevel?.transactions.map(({ id, description }) => ({id, text: description}))
      : [];

    setEditingScenarioLevelPrecheckedDesigns(edititngSceenarioLevel?.design);
    if(savedAccountItems) {
      setEditingScenarioLevelPrecheckedItems(savedAccountItems);
    } else {
      setEditingScenarioLevelPrecheckedItems([]);
    }
    refetchScenariosQuery();

    if(gameTypeShortened?.includes('equation')) {
      refetchTransactionsQuery();
    }
  }

  const gameTypeShortened = useMemo(() => {
    return (
      Object.entries(assignmentTypeMappings).find(([key]) => selectedGameType?.includes(key))?.[1] || ''
    );
  }, [selectedGameType]);

  // Resolve a single source-template scenario per game type from the
  // course-setup payload. Replaces the previous multi-source pattern
  // (entity-type + special-accounts, be-corporate + be-noncorporate,
  // principles-corporate + principles-noncorporate + principles-
  // assumptions-constraints).
  useEffect(() => {
    if (!scenarios) return;
    if (gameTypeShortened === 'classification') {
      setClassificationGameScenarioId(scenarios.find(s => s.name === 'classification-game')?.id || null);
    } else if (gameTypeShortened === 'normal') {
      setNormalGameScenarioId(scenarios.find(s => s.name === 'normal-game')?.id || null);
    } else if (gameTypeShortened === 'equation') {
      setBeGameScenarioId(scenarios.find(s => s.name === 'be-game')?.id || null);
    } else if (gameTypeShortened === 'principles') {
      setPrinciplesGameScenarioId(scenarios.find(s => s.name === 'principles-game')?.id || null);
    }
  }, [scenarios, gameTypeShortened]);

  // Ensure the source-template scenario for this game type is linked
  // to the instructor's course. Mirrors edit.js / add.js — see those
  // for the full rationale on why the instructor-scoped levels endpoint
  // returns nothing when the scenario isn't in scope.
  useEffect(() => {
    const courses = generalInfo?.courses;
    if (!scenarios || !gameTypeShortened || !courses?.length) return;

    const templateScenarioName =
      gameTypeShortened === 'classification' ? 'classification-game' :
      gameTypeShortened === 'normal' ? 'normal-game' :
      gameTypeShortened === 'equation' ? 'be-game' :
      gameTypeShortened === 'principles' ? 'principles-game' :
      null;
    if (!templateScenarioName) return;

    const templateScenario = scenarios.find(s => s.name === templateScenarioName);
    if (!templateScenario) return;

    let cancelled = false;
    (async () => {
      try {
        // Route through queryClient.fetchQuery so the probe shares the
        // React Query cache with the sibling useQuery observer for the
        // same key — a direct getInstructorScenarioLevels() call would
        // duplicate the (170KB+) request on this endpoint every mount.
        const probeLevels = await queryClient.fetchQuery(
          instructorScenarioLevelsQuery({ scenarioId: templateScenario.id })
        );
        if (cancelled) return;
        if (probeLevels?.length) return;

        await scenarioAddCourseMutation.mutateAsync({
          scenarioId: templateScenario.id,
          courseId: courses[0].id,
        });
        if (cancelled) return;
        queryClient.invalidateQueries({ queryKey: ['instructorScenarioLevels', templateScenario.id] });
      } catch {
        // Benign — leaves modal in current state if linking fails.
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarios, gameTypeShortened, generalInfo?.courses]);
  
  // See edit.js for the rationale on `normal` and on the null-return
  // when the row doesn't belong to any of the 5 element columns.
  const mapAccountQuestionToFilterItem = ({ id, order, account_item, explanation }) => {
    const relatedItemQuestionColumn = gameCreateItemsQuestionsColumns.find(column =>
      account_item.parents?.includes(column)
    );
    if (!relatedItemQuestionColumn) return null;
    return {
      id,
      order,
      text: account_item.name,
      column: relatedItemQuestionColumn,
      accountItemId: account_item.id,
      explanation,
      normal: account_item.normal,
    };
  };

  const mapBeQuestionToFilterItem = ({ transaction_id, order, description }) => ({
    id: transaction_id,
    order,
    text: description,
    column: 'Transactions',
  });

  const mapPrinciplesTransactionToFilterItem = ({ id, order, description, transaction_id }) => ({
    id,
    order,
    text: description,
    column: 'Transactions',
    accountItemId: transaction_id,
  });

  // Per-game-type filter map. Top-level keys are decision-type labels;
  // sub-buckets are template scenario levels matched to that decision
  // type per the single-source rules. Same logic as edit.js — see
  // that file's comment block for the full rule list.
  // See edit.js for the full rationale on entity-type gating.
  const targetEntityType = getTargetEntityType(generalInfo?.courses?.[0]?.businesstype);
  const itemEntityFilter = (q) =>
    matchesEntityType(q?.account_item?.entity_type, targetEntityType);

  const filterItems = useMemo(() => {
    const filterLevelsByEntity = (levels) =>
      (levels || []).filter(l => matchesEntityType(l.entity_type, targetEntityType));

    // .filter(Boolean) drops nulls the mapper returned — see edit.js.
    const buildLevelsBucket = (levels, mapItem, itemsKey) =>
      (levels || []).reduce((acc, level) => {
        acc[formatScnearioLevelToFilterName(level.name)] = (level[itemsKey] || []).map(mapItem).filter(Boolean);
        return acc;
      }, {});

    const buildSingleNamedBucket = (levels, levelName, label, mapItem, itemsKey, perItemFilter) => {
      const matched = (levels || []).find(l => l.name === levelName);
      const raw = matched ? (matched[itemsKey] || []) : [];
      const filtered = perItemFilter ? raw.filter(perItemFilter) : raw;
      return { [label]: filtered.map(mapItem).filter(Boolean) };
    };

    const buildDesignFilteredBucket = (levels, designValue, mapItem, itemsKey, perItemFilter) =>
      (levels || [])
        .filter(l => Array.isArray(l.design) && l.design.includes(designValue))
        .reduce((acc, level) => {
          const raw = level[itemsKey] || [];
          const filtered = perItemFilter ? raw.filter(perItemFilter) : raw;
          acc[formatScnearioLevelToFilterName(level.name)] = filtered.map(mapItem).filter(Boolean);
          return acc;
        }, {});

    if (gameTypeShortened === 'classification') {
      const lvl = classificationGameScenarioLevels;
      if (!lvl?.length) return {};
      return {
        'Accounts': buildSingleNamedBucket(lvl, 'classification-normal-template', 'Accounts', mapAccountQuestionToFilterItem, 'questions', itemEntityFilter),
        'Deferral & Contra Account': buildSingleNamedBucket(lvl, 'deferral-contra-template', 'Deferral & Contra Account', mapAccountQuestionToFilterItem, 'questions', itemEntityFilter),
        'Classified & Multi-Step': buildDesignFilteredBucket(lvl, '4', mapAccountQuestionToFilterItem, 'questions', itemEntityFilter),
        'Real Companies': buildDesignFilteredBucket(lvl, '5', mapAccountQuestionToFilterItem, 'questions', itemEntityFilter),
      };
    }

    if (gameTypeShortened === 'normal') {
      const lvl = normalGameScenarioLevels;
      if (!lvl?.length) return {};
      return {
        'Accounts': buildSingleNamedBucket(lvl, 'classification-normal-template', 'Accounts', mapAccountQuestionToFilterItem, 'normal_questions', itemEntityFilter),
        'Deferral & Contra Account': buildSingleNamedBucket(lvl, 'deferral-contra-template', 'Deferral & Contra Account', mapAccountQuestionToFilterItem, 'normal_questions', itemEntityFilter),
      };
    }

    if (gameTypeShortened === 'equation') {
      const lvl = filterLevelsByEntity(beGameScenarioLevels);
      if (!lvl.length) return {};
      const allLevels = buildLevelsBucket(lvl, mapBeQuestionToFilterItem, 'be_questions');
      return {
        'Increase or Decrease': allLevels,
        'Debits & Credits': allLevels,
        'Accounts Impacted': allLevels,
        'Income Statement Impact': allLevels,
        'All Types': allLevels,
      };
    }

    if (gameTypeShortened === 'principles') {
      const lvl = filterLevelsByEntity(principlesGameScenarioLevels);
      if (!lvl.length) return {};
      return {
        'Revenue & Expense Recognition': buildDesignFilteredBucket(lvl, '2', mapPrinciplesTransactionToFilterItem, 'transactions'),
        'Assumptions, Principles, & Constraints': buildDesignFilteredBucket(lvl, '4', mapPrinciplesTransactionToFilterItem, 'transactions'),
      };
    }

    return {};
  }, [
    gameTypeShortened,
    classificationGameScenarioLevels,
    normalGameScenarioLevels,
    beGameScenarioLevels,
    principlesGameScenarioLevels,
    targetEntityType,
    itemEntityFilter,
  ]);

  // Parallel map keyed identically to `filterItems`, but each leaf is
  // the level's design array (as strings). Same logic as edit.js.
  const filterItemsDesign = useMemo(() => {
    const designAsStrings = (design) =>
      Array.isArray(design)
        ? design.map(String)
        : design != null
          ? [String(design)]
          : [];

    const buildLevelsDesignMap = (levels) =>
      (levels || []).reduce((acc, { name, design }) => {
        if (!name) return acc;
        acc[formatScnearioLevelToFilterName(name)] = designAsStrings(design);
        return acc;
      }, {});

    const buildSingleNamedDesignMap = (levels, levelName, label) => {
      const matched = (levels || []).find(l => l.name === levelName);
      return { [label]: designAsStrings(matched?.design) };
    };

    const buildDesignFilteredDesignMap = (levels, designValue) =>
      (levels || [])
        .filter(l => Array.isArray(l.design) && l.design.includes(designValue))
        .reduce((acc, { name, design }) => {
          acc[formatScnearioLevelToFilterName(name)] = designAsStrings(design);
          return acc;
        }, {});

    if (gameTypeShortened === 'classification') {
      const lvl = classificationGameScenarioLevels;
      if (!lvl?.length) return {};
      return {
        'Accounts': buildSingleNamedDesignMap(lvl, 'classification-normal-template', 'Accounts'),
        'Deferral & Contra Account': buildSingleNamedDesignMap(lvl, 'deferral-contra-template', 'Deferral & Contra Account'),
        'Classified & Multi-Step': buildDesignFilteredDesignMap(lvl, '4'),
        'Real Companies': buildDesignFilteredDesignMap(lvl, '5'),
      };
    }

    if (gameTypeShortened === 'normal') {
      const lvl = normalGameScenarioLevels;
      if (!lvl?.length) return {};
      return {
        'Accounts': buildSingleNamedDesignMap(lvl, 'classification-normal-template', 'Accounts'),
        'Deferral & Contra Account': buildSingleNamedDesignMap(lvl, 'deferral-contra-template', 'Deferral & Contra Account'),
      };
    }

    if (gameTypeShortened === 'equation') {
      const lvl = beGameScenarioLevels;
      if (!lvl?.length) return {};
      const allLevels = buildLevelsDesignMap(lvl);
      return {
        'Increase or Decrease': allLevels,
        'Debits & Credits': allLevels,
        'Accounts Impacted': allLevels,
        'Income Statement Impact': allLevels,
        'All Types': allLevels,
      };
    }

    if (gameTypeShortened === 'principles') {
      const lvl = principlesGameScenarioLevels;
      if (!lvl?.length) return {};
      return {
        'Revenue & Expense Recognition': buildDesignFilteredDesignMap(lvl, '2'),
        'Assumptions, Principles, & Constraints': buildDesignFilteredDesignMap(lvl, '4'),
      };
    }

    return {};
  }, [
    gameTypeShortened,
    classificationGameScenarioLevels,
    normalGameScenarioLevels,
    beGameScenarioLevels,
    principlesGameScenarioLevels,
  ]);

  const handleScenarioLevelAccontSelectSaveButtonClick = async (selectedAccountItems, selectedFilter, selectedFilterParent, accountItemsSavedCallback, gameType, selectedDecisionType, equation) => {
    setIsLoading(true);
    let scenarioLevelId, scenarioLevelNumber;

    const selectedDesignTypes = designTypesMap[gameType]?.[selectedDecisionType] || [];
    const descriptionIndex = gameTypeShortened === 'classification' || gameTypeShortened === 'normal'
      ? 1
      : scenarioLevels.length > 2
        ? 2
        : scenarioLevels.length;

    if(typeof editingScenarioLevelIndex !== 'number') { // creating new scenario level
      let description;
      // Carry the source template's `scoring` payload onto the new level so
      // the assignment inherits the same per-question scoring config the
      // template defines (added field on /scenario-levels/).
      let templateScoring = null;
      let templateNumDecisionsQuestion = null;

      // Pick the source-template scenario for this game type. Same
      // pattern as edit.js — single source per game type.
      const sourceLevels =
        gameType === 'The Principles'   ? principlesGameScenarioLevels :
        gameType === 'Balanced Equation' ? beGameScenarioLevels :
        gameType === 'Account Classification' ? classificationGameScenarioLevels :
        gameType === 'Normal Balance' ? normalGameScenarioLevels :
        [];
      // Same gating as edit.js — BE / Principles use level-level
      // entity_type so the lookup must disambiguate; Classification /
      // Normal use account_item-level entity_type so the level lookup
      // stays name-only.
      const sourceUsesLevelEntityType = (
        gameType === 'The Principles' || gameType === 'Balanced Equation'
      );
      const filterScenarioLevel = (sourceLevels || []).find(level => {
        const nameMatches = formatScnearioLevelToFilterName(level.name) === formatScnearioLevelToFilterName(selectedFilter);
        if (!nameMatches) return false;
        if (sourceUsesLevelEntityType) {
          return matchesEntityType(level.entity_type, targetEntityType);
        }
        return true;
      });

      if(gameType === 'The Principles') {
        description = filterScenarioLevel?.description;
      } else if(gameType === 'Balanced Equation') {
        description = 'Decisions';
        switch (selectedDecisionType) {
          case 'increase-decrease':
            description += '/Increase or Decrease';
            break;
          case 'debit-credit2':
            description += '/Debits & Credits';
            break;
          case 'accounts-impacted':
            description += '/Accounts Impacted';
            break;
          case 'income-statement-impact':
            description += '/Income Statement Impact';
            break;
          case 'all-types':
            description += '/All Types';
            break;
          default:
            break;
        }
      } else {
        description = scenarioLevelsDescription[selectedGameType][descriptionIndex];
      }
      templateScoring = filterScenarioLevel?.scoring ?? null;
      templateNumDecisionsQuestion = filterScenarioLevel?.num_decisions_question ?? null;

      const resultCreateScenarioLevels = await createScenarioLevelsMutation.mutateAsync({
        name: selectedDecisionType,
        levelNumber: scenarioLevels.length + 1,
        scenarios: [scenarioId],
        description,
        design: selectedDesignTypes,
        scoring: templateScoring,
        numDecisionsQuestion: templateNumDecisionsQuestion,
        equation
      });
      scenarioLevelId = resultCreateScenarioLevels?.id;
      scenarioLevelNumber = resultCreateScenarioLevels.level;
    } else {
      scenarioLevelId = scenarioLevels[editingScenarioLevelIndex]?.id;
      scenarioLevelNumber = scenarioLevels[editingScenarioLevelIndex]?.level;
    }
    
    if(scenarioLevelId || typeof editingScenarioLevelIndex === 'number') { // editing newly crated or existing scenario level
      try {
        const remainingPreCheckedItems = [...editingScenarioLevelPrecheckedItems];

        const itemsToProcess = selectedDecisionType === 'assumptions-principles-constraints'
          ? shuffleArray([...selectedAccountItems])
          : selectedAccountItems;

        // Collect new items, then create them all in one bulk call
        // (mirrors edit.js — replaces the per-item POST loop that was
        // firing N principles-questions/ requests sequentially).
        const newItems = [];
        for (const [index, gameCreateItemQuestion] of itemsToProcess.entries()) {
          const matchedItemIndex = remainingPreCheckedItems.findIndex(
            (item) => item.text === gameCreateItemQuestion.text
          );

          if (matchedItemIndex === -1) {
            newItems.push({
              order: gameCreateItemQuestion.order || index + 1,
              accountItemId: gameCreateItemQuestion.accountItemId || gameCreateItemQuestion.id,
              explanation: gameCreateItemQuestion.explanation ?? null,
            });
          } else {
            remainingPreCheckedItems.splice(matchedItemIndex, 1);
          }
        }

        if (newItems.length) {
          console.log('[edit-tournament.js] questions bulk POST payload:', {
            gameType: gameTypeShortened,
            scenarioLevelId,
            newItemsCount: newItems.length,
            firstItem: newItems[0],
          });
          await createItemQuestionsBulkMutation.mutateAsync({
            gameType: gameTypeShortened,
            questions: newItems,
            scenarioLevelId
          });
        }

        if(remainingPreCheckedItems.length) {
          for (const remainingItem of remainingPreCheckedItems) {
            await deleteItemQuestionsMutation.mutateAsync({
              questionId: remainingItem.id,
              gameType: gameTypeShortened
            });
          }
        }
      } catch (error) {
        // Surface the failure instead of silently continuing — otherwise
        // the level shell is left orphan with 0 questions and the
        // instructor has no idea anything broke.
        const apiMessage =
          error?.response?.data?.detail
          || error?.response?.data?.message
          || (typeof error?.response?.data === 'string' ? error.response.data : '')
          || error?.message
          || 'unknown error';
        console.error('[edit-tournament.js] question mutations failed:', apiMessage, error);
        window.alert(`Could not attach questions to the new level (${gameTypeShortened}). Error: ${apiMessage}. Check the browser console for the full request payload.`);
      } finally {
        // Defense-in-depth: never let a crash in the options-seeding /
        // edit-existing-level paths leak out of the outer function —
        // otherwise the modal never closes and the Save button stays
        // stuck in its loading state.
        try {
        if(typeof editingScenarioLevelIndex !== 'number') {
          if(gameTypeShortened === 'classification') {
            // See edit.js for the full rationale. 'class-default' (Accounts)
            // and 'class-tricky' (Deferral & Contra Account) are surfaced
            // as hardcoded bucket labels in filterItems, not real level
            // names, so selectedFilter never matches a template row and
            // the option-seeding below silently produces zero options.
            const templateLookupName =
              selectedDecisionType === 'class-default' ? 'classification-normal-template' :
              selectedDecisionType === 'class-tricky'  ? 'deferral-contra-template' :
              selectedFilter.toLowerCase().replace(/\s+/g, '-');
            const filterScenarioLevel = (classificationGameScenarioLevels || []).find(level =>
              level.name.toLowerCase().replace(/\s+/g, '-') === templateLookupName
            );

            // Narrow source-template options to those whose account_item
            // matches the course's business type.
            const scenarioLevelOptions = (filterScenarioLevel?.options || [])
              .filter(itemEntityFilter);

            // Multi-step (design=4) pulls every option from the source
            // template at orders 3 through 15 — the five financial-statement
            // elements (Assets, Liabilities, Equity, Revenues, Expenses)
            // plus the section drop zones (Current/Non-Current Assets,
            // Current/Non-Current Liabilities, Gross Profit, Operating
            // Profit, Non-Operating, etc.). Revenues / Expenses don't
            // render as drop zones, but their account_item ids ride along
            // so the gameplay component can group items correctly (e.g.
            // computing the "Net Revenues" subtotal in Gross Profit).
            let filterScenarioOptions = [];
            if (selectedDecisionType === 'classified-multistep') {
              filterScenarioOptions = scenarioLevelOptions.filter(option =>
                option.order >= 3 && option.order <= 15
              );
            } else {
              switch (scenarioLevelNumber) {
                case 1:
                  filterScenarioOptions = scenarioLevelOptions.filter(option => option.order >= 1 && option.order <= 2);
                  break;
                case 2:
                  filterScenarioOptions = scenarioLevelOptions.filter(option =>
                    (option.order >= 3 && option.order <= 7)
                  );
                  break;
                default:
                  filterScenarioOptions = scenarioLevelOptions.filter(option =>
                    (option.order >= 3 && option.order <= 7)
                  );
                  break;
              }
            }
  
            try {
              const newOptions = filterScenarioOptions.map((opt, index) => ({
                order: opt.order || index + 1,
                accountItemId: opt.account_item.id,
              }));
              if (newOptions.length) {
                await createItemOptionsBulkMutation.mutateAsync({
                  gameType: gameTypeShortened,
                  options: newOptions,
                  scenarioLevelId
                });
              }
            } catch (error) {
              console.log('Some mutations failed:', error);
            }
          } else if (gameTypeShortened === 'normal') {
            const gameCreateItemsOptions = gameCreateItemsOptionsNormal;
            const allReferenceOptions = (normalGameScenarioLevels || [])
              .flatMap(lvl => lvl?.normal_options || []);

            try {
              const newOptions = gameCreateItemsOptions
                .map((name, index) => {
                  const matched = allReferenceOptions.find(opt =>
                    opt.account_item?.name === name &&
                    matchesEntityType(opt.account_item?.entity_type, targetEntityType)
                  );
                  return matched
                    ? { order: matched.order || index + 1, accountItemId: matched.account_item.id }
                    : null;
                })
                .filter(Boolean);
              if (newOptions.length) {
                await createItemOptionsBulkMutation.mutateAsync({
                  gameType: gameTypeShortened,
                  options: newOptions,
                  scenarioLevelId
                });
              }
            } catch (error) {
              console.log('Some mutations failed:', error);
            }
          } else if(gameTypeShortened === 'equation') {
            const filterScenarioLevel = (beGameScenarioLevels || []).find(level =>
              level.name.toLowerCase().replace(/\s+/g, '-') === selectedFilter.toLowerCase().replace(/\s+/g, '-') &&
              matchesEntityType(level.entity_type, targetEntityType)
            );

            let filterScenarioOptions = [];

            if (selectedDesignTypes.includes("1")) {
              filterScenarioOptions.push(...filterScenarioLevel.be_options.filter(option => option.order >= 1 && option.order <= 5));
            }

            if (selectedDesignTypes.includes("2")) {
              filterScenarioOptions.push(...filterScenarioLevel.be_options.filter(option => [1, 2, 3, 6, 7].includes(option.order)));
            }

            if (selectedDesignTypes.includes("3")) {
              filterScenarioOptions.push(
                ...filterScenarioLevel.be_options.filter(option => option.account_item.account === true)
              );
            }

            filterScenarioOptions = [...new Set(filterScenarioOptions)];
  
            try {
              const newOptions = filterScenarioOptions.map((opt, index) => ({
                order: opt.order || index + 1,
                accountItemId: opt.account_item.id,
              }));
              if (newOptions.length) {
                await createItemOptionsBulkMutation.mutateAsync({
                  gameType: gameTypeShortened,
                  options: newOptions,
                  scenarioLevelId
                });
              }
            } catch (error) {
              console.log('Some mutations failed:', error);
            }
          } else if (gameTypeShortened === 'principles' && selectedDecisionType === 'assumptions-principles-constraints') {
            const filterScenarioLevel = (principlesGameScenarioLevels || []).find(level =>
              formatScnearioLevelToFilterName(level.name) === formatScnearioLevelToFilterName(selectedFilter) &&
              matchesEntityType(level.entity_type, targetEntityType)
            );

            const sourceOptions = filterScenarioLevel?.principles_options || [];

            try {
              const newOptions = sourceOptions
                .map((opt, index) => ({
                  order: opt.order || index + 1,
                  accountItemId: opt.account_item?.id,
                }))
                .filter(o => o.accountItemId);
              if (newOptions.length) {
                await createItemOptionsBulkMutation.mutateAsync({
                  gameType: gameTypeShortened,
                  options: newOptions,
                  scenarioLevelId
                });
              }
            } catch (error) {
              console.log('Some mutations failed:', error);
            }
          }
        } else {
          let description;
          if(gameType === 'The Principles') {
            const filterScenarioLevel = (principlesGameScenarioLevels || []).find(level =>
              formatScnearioLevelToFilterName(level.name) === formatScnearioLevelToFilterName(selectedFilter)
            );
            description = filterScenarioLevel?.description;
          } else if(gameType === 'Balanced Equation') {
            description = 'Decisions';
            switch (selectedDecisionType) {
              case 'increase-decrease':
                description += '/Increase or Decrease';
                break;
              case 'debit-credit2':
                description += '/Debits & Credits';
                break;
              case 'accounts-impacted':
                description += '/Accounts Impacted';
                break;
              case 'income-statement-impact':
                description += '/Income Statement Impact';
                break;
              case 'all-types':
                description += '/All Types';
                break;
              default:
                break;
            }
          } else {
            description = scenarioLevelsDescription[gameType][descriptionIndex];
          }

          await editScenarioLevelsMutation.mutateAsync({
            scenarioLevelId,
            name: selectedDecisionType,
            description,
            design: selectedDesignTypes
          });

          if(gameType === 'Balanced Equation') { // update options for BE only
            let designsToAdd = [];
            let designsToRemove = [...editingScenarioLevelPrecheckedDesigns];
  
            for (const selectedDesign of selectedDesignTypes) {
              const matchedIndex = designsToRemove.indexOf(selectedDesign);
  
              if (matchedIndex === -1) {
                designsToAdd.push(selectedDesign);
              } else {
                designsToRemove.splice(matchedIndex, 1);
              }
            }
  
            if (designsToAdd.length) {
              const filterScenarioLevel = (beGameScenarioLevels || []).find(level =>
                level.name.toLowerCase().replace(/\s+/g, '-') === selectedFilter.toLowerCase().replace(/\s+/g, '-') &&
                matchesEntityType(level.entity_type, targetEntityType)
              );
    
              let filterScenarioOptions = [];
  
              if (designsToAdd.includes('1')) {
                filterScenarioOptions.push(...filterScenarioLevel.be_options.filter(option => option.order === 4 || option.order === 5));
              }
  
              if (designsToAdd.includes('2')) {
                filterScenarioOptions.push(...filterScenarioLevel.be_options.filter(option => option.order === 6 || option.order === 7));
              }
  
              if (designsToAdd.includes('3')) {
                filterScenarioOptions.push(
                  ...filterScenarioLevel.be_options.filter(option => option.account_item.account === true && ![1, 2, 3].includes(option.order))
                );
              }
  
              filterScenarioOptions = [...new Set(filterScenarioOptions)];
    
              try {
                const newOptions = filterScenarioOptions.map((opt, index) => ({
                  order: opt.order || index + 1,
                  accountItemId: opt.account_item.id,
                }));
                if (newOptions.length) {
                  await createItemOptionsBulkMutation.mutateAsync({
                    gameType: gameTypeShortened,
                    options: newOptions,
                    scenarioLevelId
                  });
                }
              } catch (error) {
                console.log('Some mutations failed:', error);
              }
            }
            if (designsToRemove.length) {
              const currentScenarioLevel = scenarioLevels[editingScenarioLevelIndex];
              // await deleteDesignMutation.mutateAsync({ design: remainingDesign });
              let filterScenarioOptions = [];
  
              if (designsToRemove.includes('1')) {
                filterScenarioOptions.push(
                  ...currentScenarioLevel.be_options.filter(option => option.order === 4 || option.order === 5)
                );
              }
  
              if (designsToRemove.includes('2')) {
                filterScenarioOptions.push(
                  ...currentScenarioLevel.be_options.filter(option => option.order === 6 || option.order === 7)
                );
              }
  
              if (designsToRemove.includes('3')) {
                filterScenarioOptions.push(
                  ...currentScenarioLevel.be_options.filter(option => option.account_item.account === true && ![1, 2, 3].includes(option.order))
                );
              }
  
              filterScenarioOptions = [...new Set(filterScenarioOptions)];

              try {
                for (const [index, deleteItemOption] of filterScenarioOptions.entries()) {
                  await deleteItemOptionsMutation.mutateAsync({optionId: deleteItemOption.id});
                }
              } catch (error) {
                console.log('Some mutations failed:', error);
              }
            }
          }
        }
        } catch (error) {
          console.error('[edit-tournament.js] finally-block cleanup failed:', error);
        }
      }
    }

    try {
      await refetchScenarioLevels();
    } catch (error) {
      // Refetch failure shouldn't block modal close / loading clear —
      // the mutations already ran, the UI will hydrate on next render.
      console.error('[edit-tournament.js] refetchScenarioLevels failed after save:', error);
    }
    accountItemsSavedCallback();
    setIsLoading(false);
  }

  const handleCloseButtonClick = () => {
    navigate('/instructor/settings/assignments');
  }

  const handleScenarioLevelDelete = async (scenarioLevelId, scenarioLevelDeletedCallback) => {
    deleteScenarioLevelMutation.mutate(
      { scenarioLevelId },
      {
        onSuccess: () => {
          scenarioLevelDeletedCallback && scenarioLevelDeletedCallback();
          refetchScenarioLevels();
          // Refresh every round's level list — the delete may hit any
          // round's scenarioId, not just the selected one.
          queryClient.invalidateQueries({ queryKey: ['instructorScenarioLevels'] });
        },
      }
    );
  };

  // Extract the modal's pre-checked items from a specific level object,
  // scoped to the round's game type. Mirrors the per-game-type shape
  // handling that handleShowLevelAccountsButtonClick does off the
  // currently-selected `scenarioLevels`, but sourced directly from the
  // clicked round's own level so we don't have to wait for a scenarioId
  // change to refetch the single-scenario query.
  const extractPrecheckedFromLevel = (level, gameType) => {
    if (!level) return [];
    if (gameType === 'Account Classification') {
      return (level.questions || []).map(({ id, account_item }) => ({ id, text: account_item?.name }));
    }
    if (gameType === 'Normal Balance') {
      return (level.normal_questions || []).map(({ id, account_item }) => ({ id, text: account_item?.name }));
    }
    if (gameType === 'Balanced Equation') {
      return (level.be_questions || []).map(({ id, description }) => ({ id, text: description }));
    }
    if (gameType === 'The Principles') {
      return (level.transactions || []).map(({ id, description }) => ({ id, text: description }));
    }
    return [];
  };

  const handleAddLevelForRound = (round) => {
    // Sync existing state so filterItems / filterItemsDesign are
    // derived off the right game type, and so the save handler's
    // scenarioLevels lookup targets the right scenario.
    setSelectedGameId(round.game_id);
    setSelectedGameType(round.gameType);
    setScenarioId(round.scenarioId);
    setEditingScenarioLevelIndex(null);
    setEditingScenarioLevelPrecheckedItems([]);
    setEditingScenarioLevelPrecheckedDesigns([]);
    refetchScenariosQuery();
    if (round.gameType === 'Balanced Equation') refetchTransactionsQuery();
    setEditingContext({ round, editingScenarioLevelIndex: null });
  };

  const handleEditLevelForRound = (round, levelIndex) => {
    const roundLevels = scenarioLevelsByRound[round.id] || [];
    const level = roundLevels[levelIndex];
    if (!level) return;
    setSelectedGameId(round.game_id);
    setSelectedGameType(round.gameType);
    setScenarioId(round.scenarioId);
    setEditingScenarioLevelIndex(levelIndex);
    setEditingScenarioLevelPrecheckedItems(extractPrecheckedFromLevel(level, round.gameType));
    setEditingScenarioLevelPrecheckedDesigns(level.design || []);
    refetchScenariosQuery();
    if (round.gameType === 'Balanced Equation') refetchTransactionsQuery();
    setEditingContext({ round, editingScenarioLevelIndex: levelIndex });
  };

  const handleDeleteLevelForRound = (round, levelId) => {
    handleScenarioLevelDelete(levelId, null);
  };

  const handleModalClose = () => {
    setEditingContext(null);
    setEditingScenarioLevelIndex(null);
    setEditingScenarioLevelPrecheckedItems([]);
    setEditingScenarioLevelPrecheckedDesigns([]);
    // Refresh every round's level list — the save may hit any round's
    // scenarioId, not just the selected one.
    queryClient.invalidateQueries({ queryKey: ['instructorScenarioLevels'] });
  };

  const handleAssignmentNameSave = (name) => {
    handleEditTournamentSettings({ name, formSavedCallback: null });
  };

  // Review-Session-only: the "The Elements" preset card in the level
  // modal fires this handler. Auto-creates the class-elements /
  // norm-elements Level 1 from the shared 'classification-normal-
  // template' scenario level, then closes the modal. Mirrors the
  // wizard's buildLevelsForGame(gameType='classification'|'normal')
  // level-1 branch, but adapted to hit the standalone create-level /
  // bulk-question / bulk-option endpoints instead of create-full.
  const handleElementsPresetSelected = async (round) => {
    const roundGameType = round?.gameType;
    const gameKey =
      roundGameType === 'Account Classification' ? 'classification' :
      roundGameType === 'Normal Balance' ? 'normal' :
      null;
    if (!gameKey) return;

    const currentCourse = generalInfo?.courses?.[0];
    if (!currentCourse) {
      console.error('[edit-tournament.js] Elements preset: no course loaded');
      return;
    }

    setIsLoading(true);
    try {
      const templateScenarioName =
        gameKey === 'classification' ? 'classification-game' : 'normal-game';
      const templateScenario = (scenarios || []).find(s => s.name === templateScenarioName);
      if (!templateScenario) throw new Error(`template scenario "${templateScenarioName}" not found`);

      // Share cache with the sibling useQuery observer for the same
      // scenario — direct fetch would duplicate a ~170KB payload.
      const templateLevels = await queryClient.fetchQuery(
        instructorScenarioLevelsQuery({ scenarioId: templateScenario.id })
      );
      console.log('[edit-tournament.js] Elements preset templateLevels response:', templateLevels);
      const template = (templateLevels || []).find(l => l.name === 'classification-normal-template');
      if (!template) throw new Error('classification-normal-template level not found on template scenario');
      console.log('[edit-tournament.js] Elements preset template level:', template);

      const questionsKey = gameKey === 'classification' ? 'questions' : 'normal_questions';
      const optionsKey = gameKey === 'classification' ? 'options' : 'normal_options';
      const targetEntityType = getTargetEntityType(currentCourse.businesstype);
      const matchesTarget = (item) => matchesEntityType(item?.account_item?.entity_type, targetEntityType);

      const allTemplateItems = [
        ...(template[questionsKey] || []),
        ...(template[optionsKey] || []),
      ];

      // Pull the 5 elements (Assets/Liabilities/Equity/Revenues/
      // Expenses) matching the course's entity type, in canonical order.
      const level1Questions = gameCreateItemsQuestionsColumns
        .map(name => allTemplateItems.find(it => it.account_item?.name === name && matchesTarget(it)))
        .filter(Boolean);

      const dropZoneNames = gameKey === 'classification'
        ? gameCreateItemsOptionsClassification
        : gameCreateItemsOptionsNormal;
      const level1Options = dropZoneNames
        .map(name => (template[optionsKey] || []).find(opt => opt.account_item?.name === name && matchesTarget(opt)))
        .filter(Boolean);

      // Fail loudly if the template didn't yield the expected shape —
      // creating an empty scenario-level shell would strand the
      // instructor with a "Level 1 · 0 questions" pill and no clear
      // recovery. Abort BEFORE any backend writes so nothing is
      // half-created.
      if (!level1Questions.length) {
        throw new Error(
          `template "${templateScenarioName}" / classification-normal-template yielded 0 element questions after filtering by entity_type=${targetEntityType ?? 'null'}. `
          + `Fields checked: ${questionsKey}[${template[questionsKey]?.length ?? 0}] + ${optionsKey}[${template[optionsKey]?.length ?? 0}]. `
          + `Expected names: ${gameCreateItemsQuestionsColumns.join(', ')}.`
        );
      }

      // 1. Create the scenario-level shell. scenarioLevels are per-
      // scenario, so target this round's scenarioId (the freshly-
      // created game's scenario, not the template's).
      const created = await createScenarioLevelsMutation.mutateAsync({
        name: gameKey === 'classification' ? 'class-elements' : 'norm-elements',
        levelNumber: 1,
        scenarios: [round.scenarioId],
        description: scenarioLevelsDescription[gameKey]?.[0] || '',
        design: ['1'],
        scoring: template.scoring ?? null,
        numDecisionsQuestion: template.num_decisions_question ?? null,
      });
      const newScenarioLevelId = created?.id;
      if (!newScenarioLevelId) throw new Error('scenario-level create returned no id');

      // 2. Bulk-attach questions (5 elements) + options (2 drop zones).
      await createItemQuestionsBulkMutation.mutateAsync({
        gameType: gameKey,
        questions: level1Questions.map((q, idx) => ({
          order: q.order || idx + 1,
          accountItemId: q.account_item?.id,
          explanation: q.explanation ?? null,
        })).filter(q => q.accountItemId),
        scenarioLevelId: newScenarioLevelId,
      });

      if (level1Options.length) {
        await createItemOptionsBulkMutation.mutateAsync({
          gameType: gameKey,
          options: level1Options.map((o, idx) => ({
            order: o.order || idx + 1,
            accountItemId: o.account_item?.id,
          })).filter(o => o.accountItemId),
          scenarioLevelId: newScenarioLevelId,
        });
      }

      // Refresh so the round-card level pills reflect the new level.
      queryClient.invalidateQueries({ queryKey: ['instructorScenarioLevels'] });
    } catch (error) {
      console.error('[edit-tournament.js] handleElementsPresetSelected failed:', error);
    } finally {
      setIsLoading(false);
      handleModalClose();
    }
  };

  const handleToggleGameSelection = (key) => {
    setSelectedGameKeys(prev => (
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    ));
  };

  // Wire the whole create flow: tournament + games + round setup.
  // Sequenced (not parallel) because setup/ needs both the tournament's
  // auto-created round ids and the freshly-created game ids to bind
  // them together. Runs the create-full/ POSTs one-per-game so a single
  // failure fails fast and stops before the setup call, avoiding half-
  // assigned rounds.
  const handleCreateReviewSession = async (rawName) => {
    if (isCreateSubmitting) return;
    setCreateErrorMessage('');

    const currentCourse = generalInfo?.courses?.[0];
    if (!currentCourse) {
      const msg = 'no course is loaded for this instructor';
      console.error('[edit-tournament.js] Cannot create review session:', msg);
      setCreateErrorMessage(msg);
      return;
    }

    const orderedSelected = CANONICAL_GAME_ORDER.filter(k => selectedGameKeys.includes(k));
    if (!orderedSelected.length) return;

    const name = (rawName || formData.name || '').trim();
    if (!name) return;

    // Track the current stage so a thrown/network error can be tagged with
    // what actually failed instead of a generic message.
    let stage = 'start';
    setIsCreateSubmitting(true);
    try {
      // 1. Create the tournament shell (backend auto-creates round_count
      // rounds, each without a game_id — those get bound in step 3).
      stage = 'create tournament';
      const createdTournament = await createTournamentsMutation.mutateAsync({
        name,
        courseId: currentCourse.id,
        roundCount: orderedSelected.length,
      });
      console.log('[edit-tournament.js] tournament create response:', createdTournament);
      // The tournaments/ POST returns the payload back without an id, so
      // list-fetch and pick the most recent match by name + course. Try
      // the common echoed-object shapes first in case the backend starts
      // returning the id later.
      let newTournamentId =
        createdTournament?.id
        ?? createdTournament?.tournament?.id
        ?? createdTournament?.data?.id
        ?? null;
      if (!newTournamentId) {
        const allTournaments = await fetchPageData(`/api/tournaments/tournaments/`);
        const matches = (allTournaments || []).filter(
          t => t.name === name && t.course_id === currentCourse.id
        );
        matches.sort((a, b) => (b.id || 0) - (a.id || 0));
        newTournamentId = matches[0]?.id ?? null;
        console.log('[edit-tournament.js] resolved tournament id via list-fetch fallback:', newTournamentId);
      }
      if (!newTournamentId) {
        throw new Error(`no id in response — got keys: ${Object.keys(createdTournament || {}).join(', ') || '(empty)'}, and fallback lookup found no match`);
      }

      // 2. Create one game + scenario shell per selected exercise, in
      // canonical order. Levels are intentionally empty here — the
      // instructor adds them from the edit page after the redirect.
      const createdGames = [];
      for (const gameKey of orderedSelected) {
        stage = `create-full ${gameKey}`;
        const gameTypeEnum = GAME_TYPE_TO_ENUM[gameKey];
        const assignmentName = `${name} — ${gameKey}`;
        const payload = {
          game: {
            name: assignmentName,
            description: '',
            start_time: new Date().toISOString(),
            end_time: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            points: 100,
            is_active: true,
            game_type: gameTypeEnum,
            course: currentCourse.id,
          },
          scenario: {
            name: assignmentName,
            courses: [currentCourse.id],
            is_published: true,
          },
          levels: [],
        };
        const result = await createGameFullMutation.mutateAsync({ gameType: gameKey, payload });
        if (!result?.game?.id) throw new Error(`create-full for ${gameKey} returned no game id`);
        createdGames.push({
          key: gameKey,
          gameId: result.game.id,
          scenarioId: result.scenario?.id ?? result.game?.scenario ?? null,
        });
      }

      // 3. Fetch the tournament's auto-created rounds AND teams so the
      // single setup/ call below can bind games to rounds, rename rounds
      // per the bracket sequence (Championship = last), rename teams
      // with the shuffled hardcoded team-name list, and trigger
      // student-to-team assignment — all atomically.
      stage = 'fetch rounds';
      const rounds = await getReviewSessionRounds({ tournamentId: newTournamentId });
      const sortedRounds = (rounds || []).slice().sort((a, b) => a.number - b.number);
      const roundNames = BRACKET_ROUND_NAMES.slice(-orderedSelected.length);
      const roundsPayload = sortedRounds.map((round, idx) => ({
        id: round.id,
        game_id: createdGames[idx]?.gameId,
        name: roundNames[idx],
      })).filter(r => r.game_id);

      stage = 'fetch teams';
      const teams = await getReviewSessionTeams({ tournamentId: newTournamentId });
      let teamsPayload = [];
      if (Array.isArray(teams) && teams.length) {
        const shuffled = [...REVIEW_SESSION_TEAM_NAMES].sort(() => Math.random() - 0.5);
        teamsPayload = teams
          .map((team, idx) => ({ id: team.id, name: shuffled[idx % shuffled.length] }))
          .filter(t => t.id && t.name);
      }

      stage = 'setup tournament';
      await setupTournamentMutation.mutateAsync({
        tournamentId: newTournamentId,
        rounds: roundsPayload,
        ...(teamsPayload.length ? { teams: teamsPayload } : {}),
        assignStudents: true,
      });

      // Prime the caches the destination edit page consumes so it doesn't
      // have to refetch anything already known here:
      //
      // 1. generalInfo — patch in the new games + tournament instead of
      //    invalidating (a full refetch on this endpoint is ~190KB and
      //    the only staleness is the 2 items we just created).
      // 2. tournamentsRounds/{id} — seed with the sortedRounds we already
      //    fetched, merged with the game_id + name the setup/ call just
      //    persisted. Mirrors what a fresh GET would return.
      const newGameCacheEntries = createdGames.map(cg => ({
        id: cg.gameId,
        game_type: GAME_TYPE_TO_FULL_NAME[cg.key],
        scenario: cg.scenarioId,
        course: currentCourse.id,
      }));
      queryClient.setQueryData(['generalInfo'], (old) => {
        if (!old) return old;
        return {
          ...old,
          games: [...(old.games || []), ...newGameCacheEntries],
          tournaments: [
            ...(old.tournaments || []),
            {
              id: newTournamentId,
              name,
              course_id: currentCourse.id,
              round_count: orderedSelected.length,
            },
          ],
        };
      });
      // The assignments-list page reads a trimmed variant of general-info
      // under its own cache key. Without an invalidation here, the user
      // lands back on that page (via Close) and doesn't see the new
      // review session until a hard refresh.
      queryClient.invalidateQueries({ queryKey: ['generalInfo', 'assignment'] });
      const roundsWithGames = sortedRounds.map((round, idx) => ({
        ...round,
        game_id: createdGames[idx]?.gameId,
        name: roundNames[idx],
      }));
      queryClient.setQueryData(['tournamentsRounds', newTournamentId], roundsWithGames);

      // Swap the current URL to the edit page for this new tournament so
      // back/refresh works. The instructor opens the review-session page
      // themselves from Step 4's "View Tournament" button.
      navigate(`/instructor/settings/tournament/edit/${newTournamentId}`, { replace: true });
    } catch (error) {
      // Surface the failing stage + underlying message so the user sees
      // something actionable instead of an unchanged form.
      const apiMessage =
        error?.response?.data?.detail
        || error?.response?.data?.message
        || (typeof error?.response?.data === 'string' ? error.response.data : '')
        || error?.message
        || 'unknown error';
      const summary = `failed at "${stage}" — ${apiMessage}`;
      console.error('[edit-tournament.js] handleCreateReviewSession', summary, error);
      setCreateErrorMessage(summary);
    } finally {
      setIsCreateSubmitting(false);
    }
  };

  return (
    <SubPagesLayout
      navigation={navigation}
      contentClassName="adjustment-content-container"
      showPagination={false}
      secondButtonName="Close"
      onSecondButtonClick={handleCloseButtonClick}
    >
      <TournamentEditPanel
        assignmentName={formData.name}
        onAssignmentNameSave={handleAssignmentNameSave}
        isAssignmentNameSaving={isLoading}
        roundGames={tournamentRoundGames}
        scenarioLevelsByRound={scenarioLevelsByRound}
        loadingByRound={loadingByRound}
        isGameStartedByGame={isGameStartedByGame}
        editingContext={editingContext}
        filterItems={filterItems}
        filterItemsDesign={filterItemsDesign}
        editingScenarioLevelPreCheckedItems={editingScenarioLevelPrecheckedItems}
        businessType={generalInfo?.courses?.[0]?.businesstype}
        isModalLoading={isLoading}
        onAddLevel={handleAddLevelForRound}
        onEditLevel={handleEditLevelForRound}
        onDeleteLevel={handleDeleteLevelForRound}
        onCloseModal={handleModalClose}
        onSaveLevelSelection={handleScenarioLevelAccontSelectSaveButtonClick}
        isCreateMode={isCreateMode}
        selectedGameKeys={selectedGameKeys}
        onToggleGame={handleToggleGameSelection}
        onCreateSubmit={handleCreateReviewSession}
        isCreateSubmitting={isCreateSubmitting}
        createErrorMessage={createErrorMessage}
        tournamentId={tournamentId || null}
        onElementsPresetSelected={handleElementsPresetSelected}
      />
    </SubPagesLayout>
  );
}
